/**
 * Crop Management API Service
 * 
 * This service provides methods for interacting with crop management
 * backend endpoints including species and varieties.
 */

import apiClient from './apiClient';
import type {
  CropSpecies,
  CropSpeciesListItem,
  CropSpeciesListResponse,
  CreateCropSpeciesRequest,
  CropVariety,
  CropVarietyListItem,
  CropVarietyListResponse,
  CreateCropVarietyRequest,
  CropRootstockListResponse,
} from '@/types/crops';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build query string from parameters
 */
const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ============================================================================
// Crop Species API
// ============================================================================

/**
 * Get all visible crop species (system + user's custom)
 */
export const getCropSpecies = async (params?: {
  page?: number;
  size?: number;
  preferred_lang?: string;
}): Promise<CropSpeciesListResponse> => {
  const queryString = params ? buildQueryString(params) : '';
  return apiClient.get<CropSpeciesListResponse>(`/crops/species${queryString}`);
};

/**
 * Get a specific crop species by ID
 */
export const getCropSpeciesById = async (speciesId: number): Promise<CropSpecies> => {
  return apiClient.get<CropSpecies>(`/crops/species/${speciesId}`);
};

/**
 * Create a new crop species
 */
export const createCropSpecies = async (data: CreateCropSpeciesRequest): Promise<CropSpecies> => {
  return apiClient.post<CropSpecies>('/crops/species', data);
};

/**
 * Update an existing crop species
 */
export const updateCropSpecies = async (
  speciesId: number,
  data: Partial<CreateCropSpeciesRequest>
): Promise<CropSpecies> => {
  return apiClient.put<CropSpecies>(`/crops/species/${speciesId}`, data);
};

/**
 * Delete a crop species (only custom species)
 */
export const deleteCropSpecies = async (speciesId: number): Promise<{ success: boolean }> => {
  return apiClient.delete<{ success: boolean }>(`/crops/species/${speciesId}`);
};

// ============================================================================
// Crop Variety API
// ============================================================================

/**
 * Get all varieties for a specific species
 */
export const getCropVarietiesBySpecies = async (
  speciesId: number,
  params?: {
    page?: number;
    size?: number;
    preferred_lang?: string;
  }
): Promise<CropVarietyListResponse> => {
  const queryString = params ? buildQueryString(params) : '';
  return apiClient.get<CropVarietyListResponse>(`/crops/species/${speciesId}/varieties${queryString}`);
};

/** Rootstocks of a species: the system catalogue plus the user's own. */
export const getCropRootstocksBySpecies = async (
  speciesId: number,
  params?: { preferred_lang?: string },
): Promise<CropRootstockListResponse> => {
  const queryString = params ? buildQueryString(params) : '';
  return apiClient.get<CropRootstockListResponse>(`/crops/species/${speciesId}/rootstocks${queryString}`);
};

/**
 * Get a specific crop variety by ID
 */
export const getCropVarietyById = async (varietyId: number): Promise<CropVariety> => {
  return apiClient.get<CropVariety>(`/crops/varieties/${varietyId}`);
};

/**
 * Create a new crop variety
 */
export const createCropVariety = async (data: CreateCropVarietyRequest): Promise<CropVariety> => {
  return apiClient.post<CropVariety>('/crops/varieties', data);
};

/**
 * Update an existing crop variety
 */
export const updateCropVariety = async (
  varietyId: number,
  data: Partial<CreateCropVarietyRequest>
): Promise<CropVariety> => {
  return apiClient.put<CropVariety>(`/crops/varieties/${varietyId}`, data);
};

/**
 * Delete a crop variety (only custom varieties)
 */
export const deleteCropVariety = async (varietyId: number): Promise<{ success: boolean }> => {
  return apiClient.delete<{ success: boolean }>(`/crops/varieties/${varietyId}`);
};

// ============================================================================
// Export all API functions
// ============================================================================

const cropsApi = {
  // Species
  getCropSpecies,
  getCropSpeciesById,
  createCropSpecies,
  updateCropSpecies,
  deleteCropSpecies,

  // Varieties
  getCropVarietiesBySpecies,
  getCropVarietyById,
  createCropVariety,
  updateCropVariety,
  deleteCropVariety,
};

export default cropsApi;
